import Cookies from 'js-cookie';

interface SubmissionRecord {
  timestamp: number;
  count: number;
}

const FORM_SUBMISSION_COOKIE = 'loan_form_submissions';
const MAX_SUBMISSIONS = 2;
const TIME_WINDOW_HOURS = 3;

export const checkFormSubmissionLimit = (): { canSubmit: boolean; remainingSubmissions: number; timeUntilReset: number } => {
  const cookieValue = Cookies.get(FORM_SUBMISSION_COOKIE);
  const now = Date.now();
  const timeWindowMs = TIME_WINDOW_HOURS * 60 * 60 * 1000; // 3 hours in milliseconds
  
  if (!cookieValue) {
    return {
      canSubmit: true,
      remainingSubmissions: MAX_SUBMISSIONS,
      timeUntilReset: 0
    };
  }
  
  try {
    const record: SubmissionRecord = JSON.parse(cookieValue);
    const timeSinceFirstSubmission = now - record.timestamp;
    
    // If more than 3 hours have passed, reset the counter
    if (timeSinceFirstSubmission >= timeWindowMs) {
      return {
        canSubmit: true,
        remainingSubmissions: MAX_SUBMISSIONS,
        timeUntilReset: 0
      };
    }
    
    const remainingSubmissions = Math.max(0, MAX_SUBMISSIONS - record.count);
    const timeUntilReset = timeWindowMs - timeSinceFirstSubmission;
    
    return {
      canSubmit: remainingSubmissions > 0,
      remainingSubmissions,
      timeUntilReset
    };
  } catch (error) {
    // If cookie is corrupted, reset it
    Cookies.remove(FORM_SUBMISSION_COOKIE);
    return {
      canSubmit: true,
      remainingSubmissions: MAX_SUBMISSIONS,
      timeUntilReset: 0
    };
  }
};

export const recordFormSubmission = (): void => {
  const cookieValue = Cookies.get(FORM_SUBMISSION_COOKIE);
  const now = Date.now();
  const timeWindowMs = TIME_WINDOW_HOURS * 60 * 60 * 1000;
  
  if (!cookieValue) {
    // First submission
    const record: SubmissionRecord = {
      timestamp: now,
      count: 1
    };
    Cookies.set(FORM_SUBMISSION_COOKIE, JSON.stringify(record), { 
      expires: new Date(now + timeWindowMs),
      sameSite: 'strict'
    });
    return;
  }
  
  try {
    const record: SubmissionRecord = JSON.parse(cookieValue);
    const timeSinceFirstSubmission = now - record.timestamp;
    
    if (timeSinceFirstSubmission >= timeWindowMs) {
      // Reset counter after 3 hours
      const newRecord: SubmissionRecord = {
        timestamp: now,
        count: 1
      };
      Cookies.set(FORM_SUBMISSION_COOKIE, JSON.stringify(newRecord), { 
        expires: new Date(now + timeWindowMs),
        sameSite: 'strict'
      });
    } else {
      // Increment counter within the time window
      const updatedRecord: SubmissionRecord = {
        timestamp: record.timestamp, // Keep the original timestamp
        count: record.count + 1
      };
      Cookies.set(FORM_SUBMISSION_COOKIE, JSON.stringify(updatedRecord), { 
        expires: new Date(record.timestamp + timeWindowMs),
        sameSite: 'strict'
      });
    }
  } catch (error) {
    // If cookie is corrupted, start fresh
    const record: SubmissionRecord = {
      timestamp: now,
      count: 1
    };
    Cookies.set(FORM_SUBMISSION_COOKIE, JSON.stringify(record), { 
      expires: new Date(now + timeWindowMs),
      sameSite: 'strict'
    });
  }
};

export const formatTimeUntilReset = (milliseconds: number): string => {
  const hours = Math.floor(milliseconds / (1000 * 60 * 60));
  const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};